import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  JobCategory,
  JobCategoryState,
  CreateJobCategoryRequest,
  UpdateJobCategoryRequest,
  JobCategoryListParams,
} from '@/constants/types';
import { jobCategoryService } from '@/services/jobCategory/jobCategoryService';

const initialState: JobCategoryState = {
  jobCategories: [],
  currentJobCategory: null,
  pagination: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
};

export const fetchJobCategories = createAsyncThunk(
  'jobCategory/fetchJobCategories',
  async (params: JobCategoryListParams | undefined, { rejectWithValue }) => {
    try {
      const response = await jobCategoryService.getJobCategories(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch job categories');
    }
  }
);

export const fetchJobCategory = createAsyncThunk(
  'jobCategory/fetchJobCategory',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await jobCategoryService.getJobCategory(id);
      return response.data.jobCategory;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch job category');
    }
  }
);

export const createJobCategory = createAsyncThunk(
  'jobCategory/createJobCategory',
  async (data: CreateJobCategoryRequest, { rejectWithValue }) => {
    try {
      const response = await jobCategoryService.createJobCategory(data);
      return response.data.jobCategory;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create job category');
    }
  }
);

export const updateJobCategory = createAsyncThunk(
  'jobCategory/updateJobCategory',
  async ({ id, data }: { id: string; data: UpdateJobCategoryRequest }, { rejectWithValue }) => {
    try {
      const response = await jobCategoryService.updateJobCategory(id, data);
      return response.data.jobCategory;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update job category');
    }
  }
);

export const deleteJobCategory = createAsyncThunk(
  'jobCategory/deleteJobCategory',
  async (id: string, { rejectWithValue }) => {
    try {
      await jobCategoryService.deleteJobCategory(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete job category');
    }
  }
);

const jobCategorySlice = createSlice({
  name: 'jobCategory',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetJobCategoryState: (state) => {
      state.jobCategories = [];
      state.currentJobCategory = null;
      state.pagination = null;
      state.error = null;
    },
    setCurrentJobCategory: (state, action: PayloadAction<JobCategory | null>) => {
      state.currentJobCategory = action.payload;
    },
    clearJobCategories: (state) => {
      state.jobCategories = [];
      state.pagination = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobCategories.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchJobCategories.fulfilled, (state, action) => {
        state.isLoading = false;
        state.jobCategories = action.payload.jobCategories;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchJobCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchJobCategory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchJobCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentJobCategory = action.payload;
      })
      .addCase(fetchJobCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createJobCategory.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createJobCategory.fulfilled, (state, action) => {
        state.isCreating = false;
        state.jobCategories.unshift(action.payload);
      })
      .addCase(createJobCategory.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      .addCase(updateJobCategory.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateJobCategory.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.jobCategories.findIndex(jc => jc._id === action.payload._id);
        if (index !== -1) {
          state.jobCategories[index] = action.payload;
        }
        if (state.currentJobCategory?._id === action.payload._id) {
          state.currentJobCategory = action.payload;
        }
      })
      .addCase(updateJobCategory.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      .addCase(deleteJobCategory.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteJobCategory.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.jobCategories = state.jobCategories.filter(jc => jc._id !== action.payload);
        if (state.currentJobCategory?._id === action.payload) {
          state.currentJobCategory = null;
        }
      })
      .addCase(deleteJobCategory.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  resetJobCategoryState,
  setCurrentJobCategory,
  clearJobCategories,
} = jobCategorySlice.actions;

export default jobCategorySlice.reducer;